import map from 'lodash/map'
import React, { FC, useMemo } from 'react'

import { Tiles } from '@Blocks/TilesBlock/Tiles'
import { BlockContainer } from '@Components/BlockContainer'
import { Elements } from '@Components/Elements/Types'
import { Block, BlockAppearance, PagePresentation, TileResource } from '@Generated/graphql'
import {
  convertRTEToHTML,
  getReferenceFieldValue,
} from '@Globals/CMS/FieldHelpers'
import { Image } from '@Globals/Image'

export interface TileBlockProps {
  id?: string
  presentation?: PagePresentation
  tileBlock: Block
}

interface TileButton {
  newTab?: boolean
  text?: string
  url?: string
}
export interface TilesBlockSettings {
  cardAspectRatio?: string
  cardSize?: string
  cardVariant?: string
  enableSlider?: boolean
  rowsDesktop?: number | string
  rowsMobile?: number | string
}

export interface TileProps {
  appearance: BlockAppearance
  button?: TileButton
  campaignTag?: {
    appearance: BlockAppearance
    text: string
  }
  display: string[]
  elements: Elements[] | null
  image: Image
  subTitle: string
  title: string
}

interface TilesBlockProp {
  tiles: TileProps[]
}

function isImage(item: any | Image): item is Image {
  const asImage = item as Image
  return (
    !!item
    && !!asImage.url
    && !!asImage.dimensions
    && (!!asImage.dimensions.height || !!asImage.dimensions.width)
  )
}

const getImageFromReference = (reference: any | null | undefined) => {
  if (!reference) {
    return null
  }
  if (!!reference.image && isImage(reference.image)) {
    return reference.image
  }
  return null
}

const getTileButton = (tile) => {
  const enableButton = tile.display.includes('button')
  if (!enableButton && !tile?.button?.link?.url && !tile?.button?.text) {
    return undefined
  }
  return {
    url: tile.button.link?.url,
    text: tile.button.text ?? '',
    newTab: !!tile.button.link?.newTab,
  }
}

const getTilesBlockData = ({ fields }: Block): TilesBlockProp => {
  const tiles: TileResource[] = getReferenceFieldValue(fields || [], 'tiles') ?? []

  const tileContents: TileProps[] = map(tiles, tile => ({
    appearance: tile.appearance || {},
    display: tile.display ?? [],
    campaignTag: tile.campaignTag
      ? {
        appearance: tile.campaignTag.appearance || {},
        text: convertRTEToHTML(tile.campaignTag.text),
      }
      : undefined,
    title: tile.header ?? '',
    subTitle: convertRTEToHTML(tile.description),
    image: getImageFromReference(tile.references && tile.references[0]),
    button: getTileButton(tile),
    elements: (tile.elements || null) as Elements[] | null,
  }))

  return {
    tiles: tileContents ?? [],
  }
}

const TileBlock: FC<TileBlockProps> = ({ id, presentation, tileBlock }) => {
  const { tiles } = useMemo(
    () => getTilesBlockData(tileBlock),
    [tileBlock],
  )

  const { appearance, common, settings } = tileBlock

  return (
    <BlockContainer
      appearance={appearance || {}}
      common={common || {}}
      id={id}
      presentation={presentation}
    >
      {tiles.length && (
        <Tiles
          items={tiles}
          presentation={presentation}
          settings={settings as TilesBlockSettings}
        />
      )}
    </BlockContainer>
  )
}
export default TileBlock
